import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getAnalytics, logEvent } from 'firebase/analytics';

const CalculatorHistory = ({ history = [], onClearHistory, onUseHistoryItem, highContrastMode = false, largeButtonsMode = false }) => {
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Format history for export
  const formatHistoryForExport = () => {
    return history.map((item, index) => {
      return `${index + 1}. ${item.equation}`;
    }).join('\n');
  };

  // Handle export to file
  const handleExport = () => {
    try {
      const historyText = formatHistoryForExport();
      const blob = new Blob([historyText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `calculator-history-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setExportDialogOpen(false);
      
      // Log export to Firebase Analytics
      try {
        const analytics = getAnalytics();
        logEvent(analytics, 'export_history', {
          calculator_type: 'scientific',
          history_length: history.length,
        });
      } catch (error) {
        console.error('Analytics error:', error);
      }
    } catch (error) {
      console.error('Error exporting history:', error);
    }
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = () => {
    try {
      const historyText = formatHistoryForExport();
      navigator.clipboard.writeText(historyText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
      
      // Log copy to Firebase Analytics
      try {
        const analytics = getAnalytics();
        logEvent(analytics, 'copy_history', {
          calculator_type: 'scientific',
          history_length: history.length,
        });
      } catch (error) {
        console.error('Analytics error:', error);
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  // Handle clear history confirmation
  const handleClearHistory = () => {
    if (onClearHistory) {
      onClearHistory();
      setConfirmClearOpen(false);
      
      // Log clear history to Firebase Analytics
      try {
        const analytics = getAnalytics();
        logEvent(analytics, 'clear_history', {
          calculator_type: 'scientific',
          history_length: history.length,
        });
      } catch (error) {
        console.error('Analytics error:', error);
      }
    }
  };

  // Handle using a history item
  const handleUseHistoryItem = (item) => {
    if (onUseHistoryItem) {
      onUseHistoryItem(item);
      
      // Log use history item to Firebase Analytics
      try {
        const analytics = getAnalytics();
        logEvent(analytics, 'use_history_item', {
          calculator_type: 'scientific',
        });
      } catch (error) {
        console.error('Analytics error:', error);
      }
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Calculation History
      </Typography>
      
      {/* History Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={() => setExportDialogOpen(true)}
          disabled={history.length === 0}
          sx={{
            fontSize: largeButtonsMode ? '1rem' : '0.875rem',
            bgcolor: highContrastMode ? '#333' : undefined,
            color: highContrastMode ? 'white' : undefined,
            borderColor: highContrastMode ? '#666' : undefined,
            '&:hover': {
              bgcolor: highContrastMode ? '#444' : undefined,
              borderColor: highContrastMode ? '#999' : undefined,
            },
            '&.Mui-disabled': {
              color: highContrastMode ? '#666' : undefined,
              borderColor: highContrastMode ? '#444' : undefined,
            },
          }}
        >
          Export
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<ClearAllIcon />}
          onClick={() => setConfirmClearOpen(true)}
          disabled={history.length === 0}
          sx={{
            fontSize: largeButtonsMode ? '1rem' : '0.875rem',
            bgcolor: highContrastMode ? '#331111' : undefined,
            color: highContrastMode ? '#ff8888' : undefined,
            borderColor: highContrastMode ? '#662222' : undefined,
            '&:hover': {
              bgcolor: highContrastMode ? '#441111' : undefined,
              borderColor: highContrastMode ? '#993333' : undefined,
            },
            '&.Mui-disabled': {
              color: highContrastMode ? '#664444' : undefined,
              borderColor: highContrastMode ? '#442222' : undefined,
            },
          }}
        >
          Clear All
        </Button>
      </Box>
      
      {/* Copy Success Alert */}
      {copySuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          History copied to clipboard!
        </Alert>
      )}
      
      {/* History List */}
      <Paper 
        elevation={1} 
        sx={{ 
          maxHeight: 300, 
          overflow: 'auto',
          bgcolor: highContrastMode ? '#222' : 'background.paper',
          border: highContrastMode ? '1px solid #666' : 'none',
        }}
      >
        {history.length > 0 ? (
          <List dense>
            {history.map((item, index) => (
              <React.Fragment key={index}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  secondaryAction={
                    <Tooltip title="Use this result">
                      <IconButton 
                        edge="end" 
                        aria-label="use" 
                        onClick={() => handleUseHistoryItem(item)}
                        sx={{
                          color: highContrastMode ? '#90caf9' : undefined,
                        }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  }
                  sx={{
                    '&:hover': {
                      bgcolor: highContrastMode ? '#333' : undefined,
                    },
                  }}
                >
                  <ListItemText
                    primary={item.equation}
                    secondary={`Result: ${item.result}`}
                    primaryTypographyProps={{
                      sx: { 
                        fontSize: largeButtonsMode ? '1rem' : '0.875rem',
                        color: highContrastMode ? 'white' : undefined,
                      },
                    }}
                    secondaryTypographyProps={{
                      sx: { 
                        fontSize: largeButtonsMode ? '0.875rem' : '0.75rem',
                        color: highContrastMode ? '#aaa' : undefined,
                      },
                    }}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                color: highContrastMode ? '#aaa' : undefined,
              }}
            >
              No calculation history yet.
            </Typography>
          </Box>
        )}
      </Paper>
      
      {/* Accessibility Info */}
      <Typography 
        variant="caption" 
        display="block" 
        sx={{ 
          mt: 2,
          color: highContrastMode ? '#aaa' : undefined,
        }}
      >
        <strong>Accessibility:</strong> Use Tab key to navigate between history items. Press Enter to use a history item.
      </Typography>
      
      {/* Clear History Confirmation Dialog */}
      <Dialog
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        aria-labelledby="clear-history-dialog-title"
        PaperProps={{
          sx: {
            bgcolor: highContrastMode ? '#222' : 'background.paper',
            color: highContrastMode ? 'white' : undefined,
          },
        }}
      >
        <DialogTitle id="clear-history-dialog-title">
          Clear Calculation History
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to clear all calculation history? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmClearOpen(false)}
            sx={{
              color: highContrastMode ? '#aaa' : undefined,
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleClearHistory} 
            color="error" 
            autoFocus
            sx={{
              color: highContrastMode ? '#ff8888' : undefined,
            }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Export Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        aria-labelledby="export-dialog-title"
        PaperProps={{
          sx: {
            bgcolor: highContrastMode ? '#222' : 'background.paper',
            color: highContrastMode ? 'white' : undefined,
          },
        }}
      >
        <DialogTitle id="export-dialog-title">
          Export Calculation History
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Choose how you would like to export your calculation history:
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              sx={{
                bgcolor: highContrastMode ? '#1a3c5a' : undefined,
                '&:hover': {
                  bgcolor: highContrastMode ? '#265580' : undefined,
                },
              }}
            >
              Save as File
            </Button>
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyToClipboard}
              sx={{
                bgcolor: highContrastMode ? '#1a3c5a' : undefined,
                '&:hover': {
                  bgcolor: highContrastMode ? '#265580' : undefined,
                },
              }}
            >
              Copy to Clipboard
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setExportDialogOpen(false)}
            sx={{
              color: highContrastMode ? '#aaa' : undefined,
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalculatorHistory;
